import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { LocalStorageKeys } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import {
  useConfiguration,
  useStoreCurrencies,
  useUpdatePreferences,
} from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  AttributeType,
  PreferenceFilter,
  PreferenceOption,
  PriceRange,
  SearchFunctionProperties,
  SearchTerm,
  SearchTermType,
} from "@is/types";
import {
  getAppliedPreferenceOptions,
  getPrimaryAttributesWithSelectedSearchTerms,
  getSelectedTab,
  getSortedSearchTermsByLastRemove,
  unselectSingleSelectedAttributes,
} from "@is/utils";

const useManageNewSearchDrawer = (
  handleSearch: ({
    newSearchTerms,
    pageNumber,
    sectionId,
  }: SearchFunctionProperties) => void
) => {
  const { attributes, isLoading } = useConfiguration();

  const { searchTerms, updateViewState, tabs, selectedTab } =
    useContext(ViewStateContext).content;

  const { currencies } = useStoreCurrencies();
  const countryCode = LocalStorageService.getItem(LocalStorageKeys.CountryCode);

  const currencyCode = useMemo(
    () =>
      currencies?.find((c) => c.countryCode === countryCode)?.currencyCode ??
      "",
    [currencies, countryCode]
  );

  const [
    { data: preferences, isPending: arePreferencesPending },
    updatePreferences,
  ] = useUpdatePreferences();

  const lastRequestedPreferencesRef = useRef<string>(null);

  const filters: PreferenceFilter[] = useMemo(
    () =>
      isLoading
        ? undefined
        : attributes
            ?.filter((attribute) => !attribute.custom)
            .map(({ key }) => ({
              key,
              sortBy: "Label",
            })),
    [isLoading, attributes]
  );

  const filteredPreferences = useMemo(
    () =>
      preferences?.filter(
        (preference) =>
          tabs?.some((tab) => tab.key === preference.key) &&
          preference?.key !== SearchTermType.Length
      ),
    [preferences, tabs]
  );

  const primaryAttributes = useMemo(
    () => attributes?.filter((attribute) => attribute.primary),
    [attributes]
  );

  const singleSelectionAttributes = useMemo(
    () => attributes?.filter((attribute) => attribute.singleSelection),
    [attributes]
  );

  const handlePreferenceClick = useCallback(
    async (preferenceOption?: PreferenceOption) => {
      const hasPreferenceToRemove = searchTerms.some(
        (searchTerm: SearchTerm) =>
          searchTerm.value === preferenceOption.key &&
          searchTerm.type === preferenceOption.type &&
          searchTerm.categoryKey === preferenceOption.categoryKey
      );

      const selectedSearchTag = {
        key: preferenceOption.parentKey,
        value: preferenceOption.key,
        type: preferenceOption.type,
        label: preferenceOption.label,
        isPrimary: primaryAttributes.some(
          (attribute) => attribute.key === preferenceOption.parentKey
        ),
        ...(preferenceOption.categoryKey && {
          categoryKey: preferenceOption.categoryKey,
        }),
      };

      let newSearchTerms = [];

      if (hasPreferenceToRemove) {
        const filteredSearchTerms = getSortedSearchTermsByLastRemove(
          searchTerms,
          preferences,
          selectedSearchTag
        );

        newSearchTerms = [...filteredSearchTerms];
      } else {
        const filteredSearchTerms = unselectSingleSelectedAttributes(
          preferenceOption.parentKey,
          preferenceOption.type,
          singleSelectionAttributes,
          searchTerms,
          preferences
        );

        newSearchTerms = [...filteredSearchTerms, selectedSearchTag];
      }

      const newSearchTermsIncludeLength = newSearchTerms.some(
        (term) => term.type === SearchTermType.Length
      );

      const newSearchTermsIncludeSizes = newSearchTerms.some(
        (term) => term.type === SearchTermType.Size
      );

      if (!newSearchTermsIncludeSizes && newSearchTermsIncludeLength) {
        newSearchTerms = newSearchTerms.filter(
          (term) => term.type !== SearchTermType.Length
        );
      }

      let nextTab;

      const hasLengthPreference = preferences?.find(
        (preference) => preference?.key === AttributeType.Length
      )?.options;

      if (
        preferenceOption.type === SearchTermType.Size &&
        !hasLengthPreference?.length
      ) {
        nextTab = getSelectedTab(filteredPreferences, newSearchTerms);
      } else if (preferenceOption.type === SearchTermType.Size) {
        nextTab = selectedTab;
      } else {
        nextTab = getSelectedTab(filteredPreferences, newSearchTerms);
      }

      updateViewState({
        searchTerms: newSearchTerms,
        selectedTab: nextTab ?? selectedTab,
      });

      await updatePreferences({
        filters,
        appliedPreferenceOptions: getAppliedPreferenceOptions(
          newSearchTerms,
          preferences
        ),
      });

      trackEvent({
        eventType: hasPreferenceToRemove
          ? TrackingEventTypes.SearchTermRemoved
          : TrackingEventTypes.SearchTermAdded,
        data: {
          terms: [
            {
              key: preferenceOption.parentKey,
              value: preferenceOption.key,
              type: preferenceOption.type,
              label: preferenceOption.label,
            },
          ],
        },
      });

      handleSearch({ newSearchTerms });
    },
    [
      singleSelectionAttributes,
      filteredPreferences,
      filters,
      handleSearch,
      preferences,
      primaryAttributes,
      searchTerms,
      selectedTab,
      updatePreferences,
      updateViewState,
    ]
  );

  const handlePriceRangeSearch = useCallback(
    (range: PriceRange) => {
      const roundedMin = range.min != null ? Math.round(range.min) : undefined;
      const roundedMax = range.max != null ? Math.round(range.max) : undefined;
      const priceLabel = `${roundedMin}-${roundedMax} ${currencyCode}`;

      // Remove existing price term if any
      const searchTermsWithoutPrice = searchTerms.filter(
        (term) => term.type !== SearchTermType.Price
      );

      // Add new price term
      const newPriceTerm: SearchTerm = {
        type: SearchTermType.Price,
        key: SearchTermType.Price,
        value: "price",
        label: priceLabel,
        min: roundedMin,
        max: roundedMax,
        currency: currencyCode || undefined,
        country: countryCode ?? undefined,
      };

      const newSearchTerms = [...searchTermsWithoutPrice, newPriceTerm];

      updateViewState({
        searchTerms: newSearchTerms,
        priceRange: range,
      });

      trackEvent({
        eventType: TrackingEventTypes.SearchTermAdded,
        data: {
          terms: [newPriceTerm],
        },
      });

      handleSearch({ newSearchTerms });
    },
    [currencyCode, countryCode, searchTerms, updateViewState, handleSearch]
  );

  useEffect(() => {
    if (primaryAttributes && filters) {
      const appliedPreferenceOptions =
        getPrimaryAttributesWithSelectedSearchTerms(
          primaryAttributes,
          searchTerms
        );

      const preparedOptions = appliedPreferenceOptions.reduce(
        (acc: PreferenceOption[], attribute) => {
          if (!attribute.options.length) return acc;

          acc.push({
            key: attribute.key,
            ...(attribute.options.length
              ? {
                  options: attribute.options.map(({ key }) => ({
                    key,
                  })),
                }
              : {}),
          });
          return acc;
        },
        []
      );

      const params = {
        filters: filters.length ? filters : null,
        appliedPreferenceOptions: searchTerms.length ? preparedOptions : null,
      };

      const signature = JSON.stringify(params);

      // The dependencies here change identity far more often than the request
      // they describe — a configuration without attributes recreates both of
      // them on every render — and dispatching re-renders, so an unguarded
      // request feeds itself. Only the payload matters, so skip a repeat of
      // the one just sent. The signature is stored before dispatching, so an
      // in-flight or failed request cannot re-trigger this either. Filter
      // changes the shopper makes go through handlePreferenceClick and the
      // SearchBox handlers, which request directly and are never deduplicated.
      if (signature === lastRequestedPreferencesRef.current) {
        return;
      }

      lastRequestedPreferencesRef.current = signature;

      updatePreferences(params);
    }
  }, [primaryAttributes, filters]);

  return {
    attributes,
    preferences,
    arePreferencesPending,
    handlePreferenceClick,
    handlePriceRangeSearch,
    filters,
    updatePreferences,
    currencyCode,
  };
};

export default useManageNewSearchDrawer;
